import { useEffect, useState } from 'react';
import { eventAPI } from '../lib/eventApi';
import { ladderAPI, matchAPI } from '../lib/api';
import { tournamentAPI } from '../lib/tournamentApi';
import { roundRobinAPI } from '../lib/roundRobinApi';
import { clubEventsAPI } from '../lib/clubEventsApi';

/**
 * Shared "My Events" data hook.
 *
 * Returns the user's club roster as a single ordered list:
 *   1. Men's Singles Ladder entry (if the user has a real rank > 0)
 *   2. Court Movement sessions where the user has an active registration
 *   3. Tournaments + Round Robins where the viewer is an entry or alternate
 *   4. Club Events where the viewer has an active RSVP (payment status is
 *      informational only — RSVP alone qualifies for My Events)
 *
 * For in-progress sessions, also attempts to enrich each session with the
 * user's current court_number by fetching the active week (best-effort —
 * silently falls back to a plain "Week N of M" sub-line on failure).
 *
 * Consumed by:
 *   • Home page accordion (caps display to 3)
 *   • Events page "My Events" tab (no cap)
 */
export default function useMyEvents({ user, player, hasLadderAccess, managedSessions = [] }) {
  const [state, setState] = useState({ loading: true, ladder: null, sessions: [], tournaments: [], roundRobins: [], clubEvents: [] });

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      // 1. Court Movement sessions — sessions the user is registered in.
      let sessions = [];
      try {
        const r = await eventAPI.listMySessions();
        sessions = r.data?.sessions || [];
      } catch {/* ignore */}

      // 1b. Pull in any managed sessions the user is NOT registered in, so
      // Event Managers see their managed sessions even when they aren't a
      // player on them. We fetch each missing session individually; the
      // list is small (typically 0-1).
      const knownIds = new Set(sessions.map((s) => s.id));
      const missingManaged = (managedSessions || []).filter((id) => !knownIds.has(id));
      if (missingManaged.length) {
        await Promise.all(
          missingManaged.map(async (sid) => {
            try {
              const r = await eventAPI.getSession(sid);
              const s = r.data?.session;
              if (s && ['registration_open', 'registration_closed', 'in_progress'].includes(s.status)) {
                sessions.push(s);
              }
            } catch {/* ignore */}
          }),
        );
      }

      // 1a. Enrich in-progress sessions with court_number from the live week.
      const inProgress = sessions.filter((s) => s.status === 'in_progress');
      await Promise.all(
        inProgress.map(async (s) => {
          try {
            const w = await eventAPI.getWeek(s.id, s.current_week);
            const courts = w.data?.courts || [];
            for (const c of courts) {
              const found = (c.slots || []).find(
                (slot) => slot.profile_id === user.id,
              );
              if (found) {
                s.my_court_number = c.court_number;
                break;
              }
            }
          } catch {/* ignore */}
        }),
      );

      // 2. Ladder entry — only if the user has a real rank.
      let ladder = null;
      if (hasLadderAccess && player?.id) {
        try {
          const lr = await ladderAPI.getAll();
          const all = lr.data?.players || [];
          const myRow = all.find((p) => p.player_id === player.id);
          if (myRow && myRow.rank && myRow.rank > 0) {
            // Recent match sub-line — non-fatal.
            let recent = null;
            try {
              const mr = await matchAPI.getMy();
              const matches = mr.data?.matches || [];
              const finalized = matches.find((m) =>
                m.status === 'approved' || m.status === 'rejected',
              );
              if (finalized) {
                const iWon = finalized.winner_id === player.id;
                const oppName =
                  (finalized.player1_id === player.id
                    ? finalized.player2_name
                    : finalized.player1_name) || 'opponent';
                recent = {
                  outcome: iWon ? 'Won' : 'Lost',
                  preposition: iWon ? 'vs' : 'to',
                  opponent: oppName,
                };
              }
            } catch {/* ignore */}
            ladder = {
              rank: myRow.rank,
              wins: myRow.wins,
              losses: myRow.losses,
              recent,
            };
          }
        } catch {/* ignore */}
      }

      // 3. Tournaments + Round Robins where the viewer has an active entry.
      //    Prompt 2.1: registered events must appear in My Events even
      //    before competition begins (registration_open / _closed).
      //    We fetch the list endpoint and then hydrate the tournaments
      //    where the current user is player1 OR player2. The list endpoint
      //    doesn't include entry data, so we call GET /{id} for a small
      //    number of candidates. To keep this cheap, we only look at
      //    events whose status is in the upcoming/active/completed range.
      let myTournaments = [];
      let myRRs = [];
      if (user?.id) {
        const RELEVANT_STATUSES = new Set([
          'registration_open', 'registration_closed',
          'bracket_generated', 'schedule_generated',
          'in_progress',
        ]);
        try {
          const tl = await tournamentAPI.list();
          const candidates = (tl.data || []).filter((t) => RELEVANT_STATUSES.has(t.status));
          const hydrated = await Promise.all(
            candidates.map((t) => tournamentAPI.get(t.id).then((r) => r.data).catch(() => null)),
          );
          myTournaments = hydrated
            .filter(Boolean)
            .filter((t) => {
              // "Personally registered" — slot occupancy AND own timestamp.
              // Naming another member as your partner is NOT the same as
              // that member's own registration (Feb 2026 corrected model).
              const inEntry = (t.entries || []).some((e) => {
                if (e.player1_profile_id === user.id) return !!e.player1_registered_at;
                if (e.player2_profile_id === user.id) return !!e.player2_registered_at;
                return false;
              });
              const onAlt = (t.alternates || []).some(
                (a) => a.profile_id === user.id,
              );
              return inEntry || onAlt;
            })
            .map((t) => ({
              ...t,
              is_alternate: !(t.entries || []).some((e) => {
                if (e.player1_profile_id === user.id) return !!e.player1_registered_at;
                if (e.player2_profile_id === user.id) return !!e.player2_registered_at;
                return false;
              }) && (t.alternates || []).some((a) => a.profile_id === user.id),
            }));
        } catch { /* ignore */ }
        try {
          const rl = await roundRobinAPI.list();
          const candidates = (rl.data || []).filter((r) => RELEVANT_STATUSES.has(r.status));
          const hydrated = await Promise.all(
            candidates.map((r) => roundRobinAPI.get(r.id).then((rr) => rr.data).catch(() => null)),
          );
          myRRs = hydrated
            .filter(Boolean)
            .filter((rr) => {
              const inEntry = (rr.entries || []).some(
                (e) => e.player1_profile_id === user.id || e.player2_profile_id === user.id,
              );
              const onAlt = (rr.alternates || []).some(
                (a) => a.profile_id === user.id,
              );
              return inEntry || onAlt;
            })
            .map((rr) => ({
              ...rr,
              is_alternate: !(rr.entries || []).some(
                (e) => e.player1_profile_id === user.id || e.player2_profile_id === user.id,
              ) && (rr.alternates || []).some((a) => a.profile_id === user.id),
            }));
        } catch { /* ignore */ }
      }

      // 4. Club Events where the viewer has an active RSVP. Payment
      // status is informational only — RSVP alone qualifies for My
      // Events. Archived events are already filtered by the backend.
      let myClubEvents = [];
      if (user?.id) {
        try {
          const cl = await clubEventsAPI.list();
          const list = cl.data?.club_events || [];
          myClubEvents = list.filter((ce) => !!ce.viewer_rsvp);
        } catch { /* ignore */ }
      }

      if (!cancelled) setState({ loading: false, ladder, sessions, tournaments: myTournaments, roundRobins: myRRs, clubEvents: myClubEvents });
    })();

    return () => { cancelled = true; };
    // managedSessions is an array — stringify so we only re-fetch when its
    // contents actually change (not on every render that returns a new
    // array identity).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, player?.id, hasLadderAccess, JSON.stringify(managedSessions)]);

  // Unified row list. Ladder pinned first (always-on), then upcoming events
  // sorted by starts_at ascending (nulls last). Tournaments + Round Robins
  // + Court Movement sessions are all treated equally.
  const rows = [];
  if (state.ladder) rows.push({ kind: 'ladder', data: state.ladder });

  const eventRows = [];
  for (const s of state.sessions)    eventRows.push({ kind: 'session',   data: s, sortKey: s.starts_at || s.created_at || '' });
  for (const t of state.tournaments) eventRows.push({ kind: 'tournament', data: t, sortKey: t.starts_at || t.created_at || '' });
  for (const r of state.roundRobins) eventRows.push({ kind: 'round_robin', data: r, sortKey: r.starts_at || r.created_at || '' });
  for (const c of state.clubEvents)  eventRows.push({ kind: 'club_event',  data: c, sortKey: c.starts_at || c.created_at || '' });
  eventRows.sort((a, b) => {
    const A = a.sortKey || '\uffff';   // empty → sort last
    const B = b.sortKey || '\uffff';
    return A < B ? -1 : A > B ? 1 : 0;
  });
  rows.push(...eventRows);

  return {
    loading: state.loading,
    rows,
    ladder: state.ladder,
    sessions: state.sessions,
    tournaments: state.tournaments,
    roundRobins: state.roundRobins,
    clubEvents: state.clubEvents,
  };
}
